import { Navigate, Route, useParams } from 'react-router-dom'
import { PolicyGuard } from '@/components/guards/PolicyGuard'
import { ActiveInvitesStatusPage } from './pages/ActiveInvitesStatusPage'
import { DefaultPaymentLogsPage } from './pages/DefaultPaymentLogsPage'
import { ReportsHubPage } from './pages/ReportsHubPage'
import { DefaultPaymentLogEditHandoffPage } from './pages/DefaultPaymentLogEditHandoffPage'
import { MrgReportPage } from './pages/MrgReportPage'
import { PmcPerformanceRedPage } from './pages/PmcPerformanceRedPage'
import { RevenuePaymentLogsPage } from './pages/RevenuePaymentLogsPage'
import { MailLogDetailPage } from './pages/MailLogDetailPage'
import { ChargebackDetailPage } from './pages/ChargebackDetailPage'
import { PaymentLogDetailRoute } from './components/PaymentLogDetailRoute'
import { ReportListRoute } from './components/ReportListRoute'
import { REPORT_LIST_ROUTE_ENTRIES } from './constants/reportPageRegistry'
import { HIDDEN_REPORT_REACT_PATHS } from './constants/reportHubLinks'

/** Redirect deeplinks for reports that are hidden from the hub. */
function HiddenReportRedirect() {
  return <Navigate to="/reports" replace />
}

function ImapLogsListRedirect() {
  return <Navigate to="/imap-logs" replace />
}

function ImapLogsReadRedirect() {
  const { logId } = useParams<{ logId: string }>()
  return <Navigate to={`/imap-logs/read/${logId ?? ''}`} replace />
}

/** Nested detail paths for reports that remain hidden. */
const HIDDEN_REPORT_NESTED_PATHS = [
  'api-logs/:logId',
  'total-number-of-contract/:logId',
] as const

/** Child routes for `<Route path="reports" element={<Outlet />}>` */
export function renderReportChildRoutes() {
  return (
    <>
      <Route
        index
        element={
          <PolicyGuard requiredPolicies={['reports_menu']}>
            <ReportsHubPage />
          </PolicyGuard>
        }
      />

      <Route
        path="applicationInvite"
        element={
          <PolicyGuard requiredPolicies={['reports_invite']}>
            <ActiveInvitesStatusPage />
          </PolicyGuard>
        }
      />

      <Route
        path="MRG_Report"
        element={
          <PolicyGuard requiredPolicies={['reports_insurance_mrg']}>
            <MrgReportPage />
          </PolicyGuard>
        }
      />

      <Route
        path="pmc-performance-red"
        element={
          <PolicyGuard requiredPolicies={['reports_pmc_performance']}>
            <PmcPerformanceRedPage />
          </PolicyGuard>
        }
      />

      <Route
        path="default-payment-logs"
        element={
          <PolicyGuard requiredPolicies={['reports_default_payment_logs']}>
            <DefaultPaymentLogsPage />
          </PolicyGuard>
        }
      />

      <Route
        path="default-payment-logs/edit/:logId"
        element={
          <PolicyGuard requiredPolicies={['reports_default_payment_logs']}>
            <DefaultPaymentLogEditHandoffPage />
          </PolicyGuard>
        }
      />

      <Route
        path="default-payment-logs/:logId"
        element={<PaymentLogDetailRoute />}
      />

      <Route
        path="payment-logs"
        element={
          <PolicyGuard requiredPolicies={['reports_revenue_payment_logs']}>
            <RevenuePaymentLogsPage />
          </PolicyGuard>
        }
      />

      <Route
        path="payment-logs/detail/:logId"
        element={<PaymentLogDetailRoute source="payment_adjustment" />}
      />

      <Route path="payment-logs/:logId" element={<PaymentLogDetailRoute />} />

      <Route
        path="chargeback/detail/:applicantId/:logId"
        element={
          <PolicyGuard requiredPolicies={['reports_chargeback']}>
            <ChargebackDetailPage />
          </PolicyGuard>
        }
      />

      <Route
        path="mail-logs/read/:logId"
        element={
          <PolicyGuard requiredPolicies={['reports_mailgun_logs']}>
            <MailLogDetailPage variant="legacy" />
          </PolicyGuard>
        }
      />

      <Route
        path="mailgun-logs/read/:logId"
        element={
          <PolicyGuard requiredPolicies={['reports_mailgun_logs']}>
            <MailLogDetailPage variant="mailgun" />
          </PolicyGuard>
        }
      />

      <Route path="imap-logs/read/:logId" element={<ImapLogsReadRedirect />} />
      <Route path="imap-logs" element={<ImapLogsListRedirect />} />

      {REPORT_LIST_ROUTE_ENTRIES.map(({ path, policies, label }) => (
        <Route
          key={path}
          path={path}
          element={
            <ReportListRoute path={path} policies={policies} label={label} />
          }
        />
      ))}

      {/* Hidden report list + nested detail deeplinks → All Reports */}
      {[...HIDDEN_REPORT_REACT_PATHS].map((path) => (
        <Route key={`hidden-${path}`} path={path} element={<HiddenReportRedirect />} />
      ))}
      {HIDDEN_REPORT_NESTED_PATHS.map((path) => (
        <Route key={`hidden-nested-${path}`} path={path} element={<HiddenReportRedirect />} />
      ))}
    </>
  )
}
